import { useState, useCallback } from 'react';

interface UseLinkModalReturn {
  isOpen: boolean;
  url: string;
  title: string;
  showOpenInNewTab: boolean;
  allowCredentials: boolean;
  trustDomain: boolean;
  openLink: (
    url: string, 
    title?: string, 
    showOpenInNewTab?: boolean,
    allowCredentials?: boolean,
    trustDomain?: boolean
  ) => void;
  openAuthenticatedLink: (url: string, title?: string) => void;
  closeModal: () => void;
}

export const useLinkModal = (): UseLinkModalReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [showOpenInNewTab, setShowOpenInNewTab] = useState(true);
  const [allowCredentials, setAllowCredentials] = useState(true);
  const [trustDomain, setTrustDomain] = useState(false);

  const openLink = useCallback((
    newUrl: string, 
    newTitle?: string, 
    enableOpenInNewTab = true,
    enableCredentials = true,
    enableTrustDomain = false
  ) => {
    setUrl(newUrl);
    setTitle(newTitle || '');
    setShowOpenInNewTab(enableOpenInNewTab);
    setAllowCredentials(enableCredentials);
    setTrustDomain(enableTrustDomain);
    setIsOpen(true);
  }, []);

  // Convenience method for opening authenticated links with best settings for cookie preservation
  const openAuthenticatedLink = useCallback((newUrl: string, newTitle?: string) => {
    openLink(newUrl, newTitle, true, true, true);
  }, [openLink]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    // Clear URL and title after a short delay to prevent flash during closing animation
    setTimeout(() => {
      setUrl('');
      setTitle('');
      setShowOpenInNewTab(true);
      setAllowCredentials(true);
      setTrustDomain(false);
    }, 300);
  }, []);

  return {
    isOpen,
    url,
    title,
    showOpenInNewTab,
    allowCredentials,
    trustDomain,
    openLink,
    openAuthenticatedLink,
    closeModal,
  };
};
